import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Drawer } from '../../../../shared/drawer/drawer'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { useRoleSysRequestLog } from '../../../../role/hook/role-sys'
import { useAlertRoutingAccess } from './access'
import { getRun, resolveRun, retryRun } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ALERT_STATUS, DELIVERY_STATUS, SEVERITY, WordPill } from './run-words'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'

// What happened on each matched route (staff_alert_matches.outcome).
const outcomeWords = m => ({
  queued: 'sent now',
  held_paused: 'held while the route is paused',
  skipped_schedule: 'skipped (outside the schedule)',
  expired: 'expired before it could be sent',
  held_schedule: `held until ${m.heldUntil ? formatDateTime(m.heldUntil) : 'the schedule opens'}`,
  digested: m.digestID ? `in digest #${m.digestID}` : 'in a digest'
}[m.outcome] || m.outcome)

const parsePayload = text => {
  try {
    const v = JSON.parse(text || '{}')
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
  } catch (e) {
    return {}
  }
}

const show = v => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v))

// One alert (spec §20.4): what it carried, with each field's data class;
// what each matched route did with it; every delivery and its attempts;
// Retry now and Resolve.
export const RunDrawer = () => {
  const { alertID } = useParams()
  const history = useHistory()
  const catalog = useCatalog()
  const { canEdit } = useAlertRoutingAccess()
  const canOpenRequest = Boolean(useRoleSysRequestLog().accessRead)
  const [run, setRun] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  useEffect(() => { getRun(alertID).then(setRun, err => setError(serverMessage(err, "This alert couldn't be loaded."), err)) }, [alertID, setError])
  const close = () => history.push('/admin/alert-routing?tab=runs')
  const act = async fn => {
    if (busy) return
    setBusy(true)
    try { setError(null); setRun(await fn()) } catch (err) { setError(serverMessage(err, "That didn't work."), err) } finally { setBusy(false) }
  }
  if (!run) return <Drawer open title="Alert" onClose={close}>{error ? <><p className="text-danger small" role="alert">{error}</p><ErrorReference error={failure} /></> : <p className="sw-hint">Loading…</p>}</Drawer>

  const a = run.alert
  const trigger = catalog.triggers.find(t => t.kind === a.trigger) || { label: a.trigger, schema: [] }
  const schema = trigger.schema || []
  const payload = parsePayload(a.payload)
  const known = schema.filter(f => payload[f.key] !== undefined)
  const unknown = Object.keys(payload).filter(k => !schema.some(f => f.key === k))
  const deliveries = run.deliveries || []
  const matches = run.matches || []
  const retryable = deliveries.some(d => d.status === 'failed' || d.status === 'expired')

  return (
    <Drawer open size="lg" title={trigger.label} onClose={close}>
      <div className="ar-drawer">
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}
        <div className="ar-pills">
          <WordPill words={SEVERITY} value={a.severity} />
          <WordPill words={ALERT_STATUS} value={a.status} />
          {a.isTest && <Pill tone="info">Test</Pill>}
          {a.usedBackstop && <Pill tone="warn">Sent to the backstop</Pill>}
        </div>
        <p className="m-0">
          Raised <span title={formatDateTime(a.raisedAt)}>{formatAgo(a.raisedAt)}</span> by {a.actorLabel || a.actorKind || 'the system'}
          {a.requestID && canOpenRequest && <> · <Link to={`/admin/request-log/${a.requestID}?at=${encodeURIComponent(a.raisedAt)}`}>Open request</Link></>}
        </p>
        {a.resolvedAt && <p className="sw-hint m-0">Resolved {formatAgo(a.resolvedAt)}.</p>}

        <section className="ar-section" aria-label="What it carried">
          <h3 className="h6">What it carried</h3>
          {known.length + unknown.length === 0
            ? <p className="sw-hint m-0">This alert carried nothing.</p>
            : <dl className="ar-fields">
              {known.map(f => (
                <React.Fragment key={f.key}><dt>{f.label} <Pill tone={f.class === 'personal' ? 'warn' : null}>{f.class}</Pill></dt><dd>{show(payload[f.key])}</dd></React.Fragment>))}
              {unknown.map(k => <React.Fragment key={k}><dt>{k}</dt><dd>{show(payload[k])}</dd></React.Fragment>)}
            </dl>}
        </section>

        <section className="ar-section" aria-label="Routes">
          <h3 className="h6">Routes</h3>
          {matches.length === 0
            ? <p className="sw-hint m-0">No route matched{a.usedBackstop ? ', so it went to the backstop' : ''}.</p>
            : <ul className="m-0">{matches.map((m, i) => <li key={i}>{m.routeName}: {outcomeWords(m)}</li>)}</ul>}
        </section>

        <section className="ar-section" aria-label="Deliveries">
          <h3 className="h6">Deliveries</h3>
          {deliveries.length === 0 && <p className="sw-hint m-0">Nothing has been sent yet.</p>}
          {deliveries.map(d => (
            <div key={d.id} className="ar-card">
              <div className="ar-pills">
                <strong>{d.targetName || d.via}</strong>
                <WordPill words={DELIVERY_STATUS} value={d.status} />
              </div>
              <div className="sw-hint">
                to {d.destination} · {plural(d.attempts || 0, 'attempt')}
                {d.status === 'pending' && d.nextAttemptAt && <> · next {formatAgo(d.nextAttemptAt)}</>}
                {d.digestID && <> · digest #{d.digestID}, shared by every alert in its window</>}
                {d.providerMessageID && <> · id {d.providerMessageID}</>}
              </div>
              {d.lastError && <div className="sw-tone-bad small">{d.lastError}</div>}
              {(d.history || []).length > 0 && (
                <ol className="ar-timeline">{d.history.map((h, i) => (
                  <li key={i}>{formatDateTime(h.attemptedAt)}: {h.outcome} · {h.providerStatus || 'no answer'} · {h.durationMs} ms · {h.host}</li>))}</ol>
              )}
              {d.routeID && <Link to={`/admin/alert-routing/routes/${d.routeID}`}>Open route</Link>}
            </div>
          ))}
        </section>

        {canEdit && (retryable || !a.resolvedAt) && (
          <div className="ar-row">
            {retryable && <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={() => act(() => retryRun(a.id))}>Retry now</button>}
            {!a.resolvedAt && <button type="button" className="btn sw-btn" disabled={busy} onClick={() => act(() => resolveRun(a.id))}>Resolve</button>}
          </div>
        )}
        {!canEdit && retryable && <Notice tone="warn">Retrying needs the alert routing update permission, and can't be done while signed in as someone else.</Notice>}
      </div>
    </Drawer>
  )
}
